import moment from 'src/app/utils/moment';
import { safeFindIndex } from 'src/app/utils';

export interface IMealPlanStats {
  activity?: number,
  age?: number,
  bmr?: number,
  bodyfat?: number,
  height?: number,
  gender?: string,
  weight?: number
  bodyType?: string,
  maintenance?: number,
}

export interface IMealPlanDetail {
  id: string;
  title: string,
  createdBy?: string;
  creationDate?: string;
  startDate: string,
  startDateHidden?: boolean,
  endDate?: string,
  endDateHidden?: boolean,
  plans: any[],
  notes?: string[],
  stats?: IMealPlanStats,
  readNotes?: boolean,
  isPremium?: boolean;
  type?: "meal" | "macro";
}

export interface IMeal {
  uid: string;
  title: string;
  creationDate?: string;
  createdBy?: string;
  foods?: any[];
  lastModifiedDate?: string;
  lastModifiedBy?: string;
  notes?: string[];
  options?: any;
}

export interface IPlanDetail {
  /** RTDB key for a tracked day. Not stored on the node body. */
  id?: string;
  title: string;
  meals: Array<any>;
  date: string;
  trackingCompleted?: string;
  goals?: {
    calories: number,
    fat: number,
    carbs: number,
    protein: number,
  }
  totals?: any;
}

export interface IMenuLibraryItem extends Partial<IPlanDetail> {
  uid?: string;
  title: string;
  createdBy?: string;
  creationDate?: string;
  lastModifiedBy?: string;
  lastModifiedDate?: string;
  organization?: string;
}

export class MealPlan {
  id: string = null;
  title: string = null;
  comments: any[] = [];
  startDate: string = null;
  startDateHidden?: boolean = null;
  endDate?: string = null;
  endDateHidden?: boolean = null;
  plans: any[] = [];
  createdBy: string = null;
  creationDate: string = null;
  lastModifiedBy: string = null;
  lastModifiedDate: string = null;
  notes: string[] = [];
  stats: IMealPlanStats = {};
  draftMealPlan: any;
  published?: string;
  organization: string;
  readNotes: boolean;
  isPremium?: boolean;
  type?: "meal" | "macro" = "meal";
  
  constructor(init: Partial<MealPlan>) {
    Object.assign(this, init);
  }

  isPublished(): boolean {
    return this.hasOwnProperty("published") && this.hasOwnProperty("plans") && this.plans.length > 0
  }

  hasDraft(): boolean {
    return this.hasOwnProperty("draftMealPlan");
  }

  clone(): MealPlan {
    return Object.assign({}, this);
  }

  cloneActive(): IMealPlanDetail {
    const toReturn = {
      id: this.id,
      title: this.title,
      startDate: this.startDate,
      startDateHidden: this.startDateHidden,
      createdBy: this.createdBy,
      creationDate: this.creationDate,
      endDate: this.endDate,
      endDateHidden: this.endDateHidden,
      plans: this.plans,
      notes: this.notes,
      stats: this.stats,
      isPremium: this.isPremium,
      type: this.type || "meal"
    }
    return JSON.parse(JSON.stringify(toReturn))
  }

  cloneDraft(): IMealPlanDetail {
    let obj = Object.assign({}, this.draftMealPlan);
    // Fallback for browser bug
    obj.title = obj.title || this.title;
    obj.notes = obj.notes || this.notes;
    obj.createdBy = this.createdBy || null,
    obj.creationDate = this.creationDate || null,
    obj.id = this.id;
    return new MealPlan(obj)
  }

  getRecipes(): string[] {
    const foods = [];
    if (this.plans && this.plans.length > 0) {
      for (const plan of this.plans) {
        if (plan.meals && plan.meals.length > 0) {
          for (const meal of plan.meals) {
            if (meal.foods && meal.foods.length > 0) {
              for (const food of meal.foods) {
                if (food.type === "recipe") {
                  const index = safeFindIndex(foods, f => f.id === food.food);
                  if (index === -1) {
                    foods.push(food.food)
                  }
                }
              }
            }
          }
        }
      }
    }
    return foods;
  }

  getFoods(): string[] {
    const foods = [];
    if (this.plans && this.plans.length > 0) {
      for (const plan of this.plans) {
        if (plan.meals && plan.meals.length > 0) {
          for (const meal of plan.meals) {
            if (meal.foods && meal.foods.length > 0) {
              for (const food of meal.foods) {
                if (!food.type || food.type === "food") {
                  const index = foods.indexOf(food.food);
                  if (index === -1) {
                    foods.push(food.food)
                  }
                }
              }
            }
          }
        }
      }
    }
    return foods;
  }

  isStartDateValid() {
    return !this.startDate || !this.startDateHidden || (moment as any)().isSameOrAfter((moment as any)(this.startDate))
  }
    

  isEndDateValid() {
    return !this.endDate || !this.endDateHidden || (moment as any)().isSameOrBefore((moment as any)(this.endDate))
  }

  getStatus() {
    if(this.hasDraft()) {
        return "draft"
    } else if(!this.isEndDateValid()) {
        return "expired"
    } else if((moment as any)(this.startDate).isAfter((moment as any)())) {
        return "scheduled"
    } else {
        return "published"
    }
  }
}